import { App } from './app';

export enum Role {
  // Global
  SystemAdmin = 'SYSTEM_ADMIN',

  // FFM
  FfmDealer = 'FFM_DEALER',
  FfmFieldforceApproval = 'FFM_FIELDFORCE_APPROVAL',
  FfmFieldforceFinish = 'FFM_FIELDFORCE_FINISH',
  FfmMarketAdmin = 'FFM_MARKET_ADMIN',
  FfmSupervisor = 'FFM_SUPERVISOR',

  // Greenland
  GreenlandManager = 'GREENLAND_MANAGER',
  GreenlandStandard = 'GREENLAND_STANDARD',

  // Porsche Strategy 2030
  StrategyDealer = 'STRATEGY_DEALER',
  StrategyManager = 'STRATEGY_MANAGER',

  // Network Planning
  NetworkPlanningPag = 'NETWORK_PLANNING_PAG',
  NetworkPlanningMarket = 'NETWORK_PLANNING_MARKET',
  NetworkPlanningRegion = 'NETWORK_PLANNING_REGION',

  // Retail Design
  RetailDesignRegionalOfficeMarketWrite = 'RETAIL_DESIGN_REGIONAL_OFFICE_MARKET_WRITE',
  RetailDesignMarketRead = 'RETAIL_DESIGN_MARKET_READ',
  RetailDesignMarketWrite = 'RETAIL_DESIGN_MARKET_WRITE',
  RetailDesignPagRead = 'RETAIL_DESIGN_PAG_READ',
  RetailDesignPagWrite = 'RETAIL_DESIGN_PAG_WRITE',
  RetailDesignFormatApprovalRead = 'RETAIL_DESIGN_FORMAT_APPROVAL_READ',
  RetailDesignFormatApprovalWrite = 'RETAIL_DESIGN_FORMAT_APPROVAL_WRITE',

  // Survey
  SurveyPagAdmin = 'SURVEY_PAG_ADMIN',
  SurveyPagRead = 'SURVEY_PAG_READ',
  SurveyMarketAdmin = 'SURVEY_MARKET_ADMIN',
  SurveyMarketRead = 'SURVEY_MARKET_READ',
  SurveyWrite = 'SURVEY_WRITE',

  // Multisensory Experience
  MultisensoryExperiencePag = 'MULTISENSORY_EXPERIENCE_PAG',
  MultisensoryExperienceMarket = 'MULTISENSORY_EXPERIENCE_MARKET',
  MultisensoryExperienceExternal = 'MULTISENSORY_EXPERIENCE_EXTERNAL',

  // PC Roadmap
  PcRoadmapDealer = 'PC_ROADMAP_DEALER',
  PcRoadmapFieldforce = 'PC_ROADMAP_FIELDFORCE',
  PcRoadmapMarketAdmin = 'PC_ROADMAP_MARKET_ADMIN',

  // APEX
  ApexPag = 'APEX_PAG',
  ApexMarket = 'APEX_MARKET',
  ApexDealer = 'APEX_DEALER',

  // Porsche Import Agreements
  PorscheImportAgreementsPag = 'PORSCHE_IMPORT_AGREEMENTS_PAG',
  PorscheImportAgreementsWrite = 'PORSCHE_IMPORT_AGREEMENTS_WRITE',
  PorscheImportAgreementsRead = 'PORSCHE_IMPORT_AGREEMENTS_READ',

  // MPI
  Mpi = 'MPI',
}

export const rolesFor = (app: App): Role[] => {
  switch (app) {
    case App.FieldForceManagement:
      return [
        Role.FfmDealer,
        Role.FfmFieldforceApproval,
        Role.FfmFieldforceFinish,
        Role.FfmMarketAdmin,
        Role.FfmSupervisor,
      ];

    case App.Greenland:
      return [Role.GreenlandManager, Role.GreenlandStandard];

    case App.PorscheStrategy2030:
      return [Role.StrategyDealer, Role.StrategyManager];

    case App.NetworkPlanning:
      return [
        Role.NetworkPlanningPag,
        Role.NetworkPlanningMarket,
        Role.NetworkPlanningRegion,
      ];

    case App.RetailDesign:
      return [
        Role.RetailDesignRegionalOfficeMarketWrite,
        Role.RetailDesignMarketRead,
        Role.RetailDesignMarketWrite,
        Role.RetailDesignPagRead,
        Role.RetailDesignPagWrite,
        Role.RetailDesignFormatApprovalRead,
        Role.RetailDesignFormatApprovalWrite,
      ];

    case App.Survey:
      return [
        Role.SurveyPagAdmin,
        Role.SurveyPagRead,
        Role.SurveyMarketAdmin,
        Role.SurveyMarketRead,
        Role.SurveyWrite,
      ];

    case App.MultisensoryExperience:
      return [
        Role.MultisensoryExperiencePag,
        Role.MultisensoryExperienceMarket,
        Role.MultisensoryExperienceExternal,
      ];

    case App.PcRoadmap:
      return [
        Role.PcRoadmapDealer,
        Role.PcRoadmapFieldforce,
        Role.PcRoadmapMarketAdmin,
      ];

    case App.Apex:
      return [Role.ApexPag, Role.ApexMarket, Role.ApexDealer];

    case App.PorscheImportAgreements:
      return [
        Role.PorscheImportAgreementsPag,
        Role.PorscheImportAgreementsWrite,
        Role.PorscheImportAgreementsRead,
      ];

    default:
      return [];
  }
};

export const appFor = (role: Role | string): App | null => {
  switch (role) {
    // Global roles don't belong to a specific app
    case Role.SystemAdmin:
      return null;

    // FFM
    case Role.FfmDealer:
    case Role.FfmFieldforceApproval:
    case Role.FfmFieldforceFinish:
    case Role.FfmMarketAdmin:
    case Role.FfmSupervisor:
      return App.FieldForceManagement;

    // Greenland
    case Role.GreenlandManager:
    case Role.GreenlandStandard:
      return App.Greenland;

    // Porsche Strategy 2030
    case Role.StrategyDealer:
    case Role.StrategyManager:
      return App.PorscheStrategy2030;

    // Network Planning
    case Role.NetworkPlanningPag:
    case Role.NetworkPlanningMarket:
    case Role.NetworkPlanningRegion:
      return App.NetworkPlanning;

    // Retail Design
    case Role.RetailDesignRegionalOfficeMarketWrite:
    case Role.RetailDesignMarketRead:
    case Role.RetailDesignMarketWrite:
    case Role.RetailDesignPagRead:
    case Role.RetailDesignPagWrite:
    case Role.RetailDesignFormatApprovalRead:
    case Role.RetailDesignFormatApprovalWrite:
      return App.RetailDesign;

    // Survey
    case Role.SurveyPagAdmin:
    case Role.SurveyPagRead:
    case Role.SurveyMarketAdmin:
    case Role.SurveyMarketRead:
    case Role.SurveyWrite:
      return App.Survey;

    // Multisensory Experience
    case Role.MultisensoryExperiencePag:
    case Role.MultisensoryExperienceMarket:
    case Role.MultisensoryExperienceExternal:
      return App.MultisensoryExperience;

    // PC Roadmap
    case Role.PcRoadmapDealer:
    case Role.PcRoadmapFieldforce:
    case Role.PcRoadmapMarketAdmin:
      return App.PcRoadmap;

    // APEX
    case Role.ApexPag:
    case Role.ApexMarket:
    case Role.ApexDealer:
      return App.Apex;

    // Porsche Import Agreements
    case Role.PorscheImportAgreementsPag:
    case Role.PorscheImportAgreementsWrite:
    case Role.PorscheImportAgreementsRead:
      return App.PorscheImportAgreements;

    default:
      return null;
  }
};
